// Demand by country: Google Trends interest for every brand in ~250 countries,
// a composite index with Apple / Wordstat / Bing, and whether the brand's name
// already shows up in the local Google Play top-10. Ported from the radar's
// trends-results page; the whole dataset is filtered here in the browser.
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { getRadarDemand, type DemandBrand, type DemandData } from '@/api/radar';
import { useAuthStore } from '@/store/auth';
import { FREELANCER_RANKING, downloadCsv, fmtDate, weightedInterest } from './logic';
import { LoadError, Loading, Pager, SelectInput, TextInput } from './controls';

type Competition = 'yes' | 'no' | 'unknown';
const COMPETITION_LABEL: Record<Competition, string> = {
  yes: 'Name matches found', no: 'No matches in top 10', unknown: 'Not fully checked / error',
};
type Sort = 'name' | 'composite' | 'coverage' | 'score' | 'date';

interface Filters {
  q: string; geo: string; min: string; coverage: string; sort: Sort; index10: boolean;
  competition: '' | Competition; interestMin: string; freelancers: '' | '5' | '10' | '20'; size: '50' | '100' | '200';
}
const EMPTY: Filters = {
  q: '', geo: '', min: '1', coverage: '0', sort: 'name', index10: false, competition: '', interestMin: '0', freelancers: '', size: '50',
};

const fmt = (n: number) => n.toLocaleString('en-US');

export function DemandTab() {
  const data = useQuery({ queryKey: ['radar', 'demand'], queryFn: getRadarDemand, staleTime: Infinity });
  if (data.isLoading) return <Loading what="demand by country" />;
  if (data.error || !data.data) return <LoadError error={data.error} />;
  return <DemandTable data={data.data} />;
}

function DemandTable({ data }: { data: DemandData }) {
  const [f, setF] = useState<Filters>(EMPTY);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<DemandBrand | null>(null);
  const set = <K extends keyof Filters>(key: K, value: Filters[K]) => { setF((x) => ({ ...x, [key]: value })); setPage(1); };
  // Where our freelancers work is ours to know: clients never see this filter,
  // and whatever it holds is ignored for them.
  const isClient = useAuthStore((s) => s.isClient());
  const freelancers = isClient ? '' : f.freelancers;

  const countries = useMemo(() => {
    let names: Intl.DisplayNames | undefined;
    try { names = new Intl.DisplayNames(['en'], { type: 'region' }); } catch { /* falls back to the English name */ }
    return data.countries.map((c) => ({ ...c, label: names?.of(c.code) || c.name }));
  }, [data]);
  const countryIndex = useMemo(() => new Map(countries.map((c, i) => [c.code, i])), [countries]);
  const brandIndex = useMemo(() => new Map(data.brands.map((b, i) => [b, i])), [data]);
  const N = countries.length;

  // Composite score per brand × country, NaN where Trends has no value.
  const scores = useMemo(() => {
    const out = new Float32Array(data.brands.length * N);
    data.brands.forEach((b, bi) => {
      countries.forEach((c, i) => {
        const a = b.interest[c.code] ?? [];
        out[bi * N + i] = weightedInterest(b.values[i], a[0] ?? null, a[1] ?? null, a[2] ?? null)?.score ?? NaN;
      });
    });
    return out;
  }, [data, countries, N]);
  const score = (b: DemandBrand, i: number) => scores[brandIndex.get(b)! * N + i]!;
  const interest = (b: DemandBrand, i: number) => {
    const a = b.interest[countries[i]!.code] ?? [];
    return weightedInterest(b.values[i], a[0] ?? null, a[1] ?? null, a[2] ?? null);
  };

  const allowed = useMemo(() => {
    const top = freelancers ? new Set(FREELANCER_RANKING.slice(0, Number(freelancers))) : null;
    return countries.map((c) => !top || top.has(c.code));
  }, [countries, freelancers]);
  const visible = (b: DemandBrand, i: number) => {
    const s = score(b, i);
    return allowed[i]! && (f.index10 ? s >= 10 : s > 0);
  };
  const coverage = useMemo(() => {
    const out = new Int16Array(data.brands.length);
    data.brands.forEach((_, bi) => {
      let n = 0;
      for (let i = 0; i < N; i++) {
        const s = scores[bi * N + i]!;
        if (allowed[i] && (f.index10 ? s >= 10 : s > 0)) n++;
      }
      out[bi] = n;
    });
    return out;
  }, [data, scores, allowed, f.index10, N]);
  const cov = (b: DemandBrand) => coverage[brandIndex.get(b)!]!;
  const coverageLabel = f.index10 ? 'countries with composite index ≥ 10' : 'countries with composite index > 0';

  const geoIdx = f.geo ? countryIndex.get(f.geo) : undefined;
  const geo = geoIdx === undefined ? '' : f.geo;

  const competition = (b: DemandBrand, code = geo): Competition => {
    const codes = code ? [code] : countries.filter((_, i) => allowed[i] && (f.index10 ? visible(b, i) : b.values[i]! >= 10)).map((c) => c.code);
    const rows = codes.map((c) => b.play[c]);
    if (rows.some((r) => r?.state === 'done' && r.apps.length)) return 'yes';
    return rows.length && rows.every((r) => r?.state === 'done') ? 'no' : 'unknown';
  };

  const filtered = useMemo(() => {
    const q = f.q.trim().toLowerCase();
    const min = Math.max(0, Math.min(100, Number(f.min) || 0));
    const minCov = Number(f.coverage) || 0;
    const minInterest = Number(f.interestMin) || 0;
    const sort: Sort = !geo && (f.sort === 'composite' || f.sort === 'score') ? 'name' : f.sort;
    const rows = data.brands.filter((b) => (b.name + ' ' + b.query).toLowerCase().includes(q)
      && (!f.competition || competition(b) === f.competition)
      && (!geo || !minInterest || (score(b, geoIdx!) || -1) >= minInterest)
      && (!freelancers || cov(b) > 0)
      && cov(b) >= minCov
      && (!geo || b.values[geoIdx!]! >= min)
      && (!f.index10 || (geo ? visible(b, geoIdx!) : cov(b) > 0)));
    const geoScore = (b: DemandBrand) => { const s = score(b, geoIdx!); return Number.isNaN(s) ? -1 : s; };
    return rows.sort((a, b) => (
      sort === 'composite' ? geoScore(b) - geoScore(a)
        : sort === 'coverage' ? cov(b) - cov(a)
          : sort === 'score' ? b.values[geoIdx!]! - a.values[geoIdx!]!
            : sort === 'date' ? b.observedAt.localeCompare(a.observedAt) : 0
    ) || a.name.localeCompare(b.name, 'en'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, f, freelancers, geo, geoIdx, coverage, scores, allowed]);

  const size = Number(f.size);
  const pages = Math.max(1, Math.ceil(filtered.length / size));
  const current = Math.min(page, pages);

  const topCountries = (b: DemandBrand) => countries
    .map((c, i) => ({ c, i, v: score(b, i) }))
    .filter((x) => visible(b, x.i))
    .sort((a, b2) => b2.v - a.v)
    .slice(0, 5);

  const exportCsv = () => {
    const exportCountries = countries.map((c, i) => ({ ...c, i })).filter((c) => allowed[c.i]);
    downloadCsv('trends-brands.csv', [
      ['Brand', 'Query', 'Composite index', 'Composite index country', 'Sources', coverageLabel,
        ...(geo ? ['Country', 'Trends Score'] : []), 'Collected at', 'Google Play',
        ...(!geo ? exportCountries.map((c) => c.code + ' Trends Score') : [])],
      ...filtered.map((b) => {
        const r = geo ? interest(b, geoIdx!) : null;
        return [b.name, b.query, r?.score ?? '', r ? geo : '', r?.coverage ?? '', cov(b),
          ...(geo ? [geo, b.values[geoIdx!]] : []), b.observedAt, COMPETITION_LABEL[competition(b)],
          ...(!geo ? exportCountries.map(({ i }) => (f.index10 && !visible(b, i) ? '' : b.values[i])) : [])];
      }),
    ]);
  };

  return (
    <div className="space-y-5">
      <div className="grid gap-3 sm:grid-cols-3">
        <Stat value={fmt(data.brands.length)} label="brands in the base" />
        <Stat value={String(countries.length)} label="countries and territories" />
        <Stat value={fmt(filtered.length)} label="match current filters" />
      </div>

      <details className="bg-surface border border-border rounded-lg p-4 text-sm text-content-muted">
        <summary className="cursor-pointer font-medium text-content">How to read this data</summary>
        <div className="mt-2 space-y-2 max-w-4xl">
          <p>Trends Score 0–100 is the query's relative popularity across its own geographic distribution, not a search count. Zero can mean not enough data. The period is the last 12 months as of each collection date.</p>
          <p>Google Play: brand name matches in the top 10 in the country's local language (one language in multilingual countries). This does not confirm the app belongs to the brand. With "All countries" selected, GEOs with Trends Score ≥ 10 count; with "Composite index ≥ 10" on, GEOs with that index count. "No matches" only when every such GEO was checked. Unchecked countries and errors do not count as no competition.</p>
          <p>Composite index /100: Trends 50%, Apple 30%, Wordstat 10%, Bing 10%. A missing source's weight goes to Trends; zero counts as data. Wordstat and Bing are scaled to 0–100 by log and the 95th percentile within the country. It is a guide from combined signals, not demand volume. Pick a country to filter and sort by score.</p>
          {data.excludedQueries.length > 0 && (
            <p>{data.excludedQueries.length} ambiguous search queries are excluded from the report; the corrected queries need a new collection run.</p>
          )}
        </div>
      </details>

      <section className="bg-surface border border-border rounded-lg p-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-5 items-end">
        <TextInput label="Search brands" type="search" value={f.q} onChange={(v) => set('q', v)} placeholder="e.g. Rainbet" />
        <SelectInput
          label="Country / territory" value={geo} onChange={(v) => set('geo', v)} all="All countries"
          options={countries.map((c, i) => ({ c, i })).filter(({ i }) => allowed[i]).sort((a, b) => a.c.label.localeCompare(b.c.label, 'en')).map(({ c }) => [c.code, `${c.label} · ${c.code}`] as const)}
        />
        <TextInput label="Min. Trends Score in country" type="number" min={0} max={100} value={f.min} onChange={(v) => set('min', v)} disabled={!geo} />
        <TextInput label="Min. composite index in country" type="number" min={0} max={100} value={f.interestMin} onChange={(v) => set('interestMin', v)} disabled={!geo} />
        <TextInput label="Min. matching countries" type="number" min={0} max={250} value={f.coverage} onChange={(v) => set('coverage', v)} />
        <SelectInput label="Sort by" value={!geo && (f.sort === 'composite' || f.sort === 'score') ? 'name' : f.sort} onChange={(v) => set('sort', v)} options={[
          ['name', 'Name A–Z'],
          ...(geo ? [['composite', 'Composite index ↓'] as const] : []),
          ['coverage', 'Most countries with interest'],
          ...(geo ? [['score', 'Trends Score in country ↓'] as const] : []),
          ['date', 'Newest first'],
        ]} />
        <SelectInput label="Google Play competition" value={f.competition} onChange={(v) => set('competition', v)} all="All statuses" options={[
          ['yes', 'Name matches found'], ['no', 'No matches in top 10'], ['unknown', 'Not fully checked / error'],
        ]} />
        {!isClient && (
          <SelectInput label="Motive freelancer countries" value={f.freelancers} onChange={(v) => {
            // A country outside the chosen top drops out of the country filter.
            setF((x) => ({ ...x, freelancers: v, geo: v && x.geo && !FREELANCER_RANKING.slice(0, Number(v)).includes(x.geo) ? '' : x.geo }));
            setPage(1);
          }} all="All countries" options={[['5', 'Top 5 countries'], ['10', 'Top 10'], ['20', 'Top 20']]} />
        )}
        <SelectInput label="Per page" value={f.size} onChange={(v) => set('size', v)} options={['50', '100', '200']} />
        <label className="flex items-center gap-2 text-sm text-content self-end pb-2" title="Hide countries with a composite index below 10 and brands left with no countries">
          <input type="checkbox" checked={f.index10} onChange={(e) => set('index10', e.target.checked)} />
          Composite index ≥ 10
        </label>
      </section>

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-content-muted mr-auto">Found {fmt(filtered.length)} of {fmt(data.brands.length)}</span>
        <Button variant="secondary" onClick={() => { setF(EMPTY); setPage(1); }}>Reset filters</Button>
        <Button disabled={!filtered.length} onClick={exportCsv}>Download selection as CSV</Button>
      </div>

      <div className="bg-surface border border-border rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-content-muted bg-surface-muted">
            <tr>
              <th className="px-4 py-3">Brand / query</th>
              <th className="px-4 py-3">Composite index /100</th>
              <th className="px-4 py-3">{geo ? `Trends Score · ${geo}` : 'Countries with interest'}</th>
              <th className="px-4 py-3">Countries · composite index ↓</th>
              <th className="px-4 py-3">Google Play · top 10</th>
              <th className="px-4 py-3">Collected at</th>
            </tr>
          </thead>
          <tbody>
            {filtered.slice((current - 1) * size, current * size).map((b) => {
              const r = geo ? interest(b, geoIdx!) : null;
              return (
                <tr key={b.id} className="border-t border-border align-top hover:bg-surface-muted/50">
                  <td className="px-4 py-3">
                    <button type="button" className="font-medium text-content hover:text-primary text-left" onClick={() => setSelected(b)}>{b.name}</button>
                    <span className="block text-xs text-content-subtle">{b.query}</span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {r ? (
                      <>
                        <span className="font-semibold text-primary">{r.score.toFixed(1)}</span>
                        <span className="block text-xs text-content-subtle">{countries[geoIdx!]!.label} · {r.coverage}/4 sources</span>
                      </>
                    ) : <span className="text-xs text-content-subtle">{geo ? 'No data' : 'Pick a country'}</span>}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className="font-semibold text-primary">{geo ? b.values[geoIdx!] : cov(b)}</span>
                    {geo && <span className="block text-xs text-content-subtle">{cov(b)} {coverageLabel}</span>}
                  </td>
                  <td className="px-4 py-3 min-w-[220px] max-w-[520px]">
                    {topCountries(b).map(({ c, v }) => (
                      <a
                        key={c.code}
                        className="inline-block bg-primary/10 text-primary rounded px-1.5 py-0.5 m-0.5 text-xs hover:underline"
                        target="_blank" rel="noopener noreferrer"
                        title={`Search Google Play · ${c.playLang}`}
                        href={`https://play.google.com/store/search?c=apps&q=${encodeURIComponent(b.query)}&gl=${c.code.toLowerCase()}&hl=${encodeURIComponent(c.playLang)}`}
                      >
                        {c.label} {v.toLocaleString('en-US', { maximumFractionDigits: 1 })}
                      </a>
                    ))}
                    {!topCountries(b).length && <span className="text-xs text-content-subtle">No non-zero values</span>}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">{COMPETITION_LABEL[competition(b)]}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-content-muted">{fmtDate(b.observedAt, { dateStyle: 'short', timeStyle: 'short' })}</td>
                </tr>
              );
            })}
            {!filtered.length && <tr><td colSpan={6} className="px-4 py-8 text-center text-content-subtle">No brands match these filters</td></tr>}
          </tbody>
        </table>
      </div>
      <Pager page={current} pages={pages} onPage={setPage} />
      <p className="text-xs text-content-subtle">
        Collected: {fmtDate(data.first, { dateStyle: 'short', timeStyle: 'short' })} — {fmtDate(data.last, { dateStyle: 'short', timeStyle: 'short' })}. Click a brand to see all countries.
      </p>

      {selected && (
        <BrandDetail
          brand={selected}
          data={data}
          countries={countries}
          coverageText={`${cov(selected)} ${coverageLabel}`}
          index10={f.index10}
          allowed={allowed}
          visible={(i) => visible(selected, i)}
          score={(i) => score(selected, i)}
          interest={(i) => interest(selected, i)}
          competition={(code) => competition(selected, code)}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}

function Stat({ value, label }: { value: string; label: string }) {
  return (
    <div className="bg-surface border border-border rounded-lg px-4 py-3">
      <p className="text-2xl font-semibold text-primary">{value}</p>
      <p className="text-sm text-content-muted">{label}</p>
    </div>
  );
}

type Country = DemandData['countries'][number] & { label: string };

function BrandDetail({ brand: b, data, countries, coverageText, index10, allowed, visible, score, interest, competition, onClose }: {
  brand: DemandBrand; data: DemandData; countries: Country[]; coverageText: string; index10: boolean; allowed: boolean[];
  visible: (i: number) => boolean; score: (i: number) => number;
  interest: (i: number) => ReturnType<typeof weightedInterest>; competition: (code: string) => Competition; onClose: () => void;
}) {
  const [q, setQ] = useState('');
  const [zeros, setZeros] = useState(false);
  const rows = countries
    .map((c, i) => ({ c, i, v: b.values[i]! }))
    .filter((x) => allowed[x.i] && (index10 ? visible(x.i) : zeros || visible(x.i))
      && `${x.c.label} ${x.c.name} ${x.c.code}`.toLowerCase().includes(q.trim().toLowerCase()))
    .sort((a, c) => ((score(c.i) || 0) - (score(a.i) || 0)) || a.c.label.localeCompare(c.c.label, 'en'));
  const n = (v: number | null | undefined) => (v == null ? 'no data' : Number(v).toFixed(1));

  const exportCsv = () => downloadCsv('trends-brand-countries.csv', [
    ['Brand', 'Country', 'Code', 'Trends Score', 'Composite index', 'Sources', 'Trends weight %', 'Apple', 'Wordstat norm.', 'Bing norm.', 'Wordstat queries', 'Bing searches', 'Collected at', 'Google Play', 'Matched apps'],
    ...rows.map((x) => {
      const r = interest(x.i);
      return [b.name, x.c.label, x.c.code, x.v, r?.score ?? '', r?.coverage ?? '', r?.trendsWeight ?? '',
        ...(b.interest[x.c.code] ?? Array(5).fill('')).map((v) => v ?? ''), b.observedAt, COMPETITION_LABEL[competition(x.c.code)],
        (b.play[x.c.code]?.apps ?? []).map((a) => `${a.title} [${a.id}]`).join('; ')];
    }),
  ]);

  return (
    <Modal isOpen onClose={onClose} title={b.name} size="6xl">
      <div className="space-y-4 text-sm">
        <p className="text-content-muted">
          Query: {b.query} · {coverageText} · {fmtDate(b.observedAt, { dateStyle: 'short', timeStyle: 'short' })}
          {' · '}Apple: {data.periods.apple} · Wordstat: {data.periods.wordstat} · Bing: {data.periods.bing}
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[200px]"><TextInput label="Search country or code" type="search" value={q} onChange={setQ} /></div>
          <label className="flex items-center gap-2 pb-2">
            <input type="checkbox" checked={zeros} disabled={index10} onChange={(e) => setZeros(e.target.checked)} />
            Show zeros
          </label>
          <Button variant="secondary" onClick={exportCsv}>CSV</Button>
        </div>
        <div className="border border-border rounded-lg overflow-x-auto max-h-[60vh]">
          <table className="w-full">
            <thead className="text-left text-xs text-content-muted bg-surface-muted sticky top-0">
              <tr>
                <th className="px-3 py-2">Country</th><th className="px-3 py-2">Code</th><th className="px-3 py-2">Trends Score</th>
                <th className="px-3 py-2">Composite index · sources</th><th className="px-3 py-2">Google Play · name matches</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ c, i, v }) => {
                const r = interest(i);
                const a = b.interest[c.code] ?? [];
                const play = b.play[c.code];
                return (
                  <tr key={c.code} className="border-t border-border align-top">
                    <td className="px-3 py-2">{c.label}</td>
                    <td className="px-3 py-2">{c.code}</td>
                    <td className="px-3 py-2 font-semibold text-primary">{v}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {r ? (
                        <>
                          <span className="font-semibold text-primary">{r.score.toFixed(1)}</span> · {r.coverage}/4
                          <span className="block text-xs text-content-subtle">Trends {v} × {r.trendsWeight}%</span>
                          <span className="block text-xs text-content-subtle">Apple {n(a[0])}{a[0] != null ? ' × 30%' : ''}</span>
                          <span className="block text-xs text-content-subtle">Wordstat {n(a[1])}{a[1] != null ? ` × 10% (${a[3]} queries)` : ''}</span>
                          <span className="block text-xs text-content-subtle">Bing {n(a[2])}{a[2] != null ? ` × 10% (${a[4]} searches)` : ''}</span>
                        </>
                      ) : 'No Trends data'}
                    </td>
                    <td className="px-3 py-2 min-w-[260px]">
                      {!play ? 'Not checked' : play.state !== 'done'
                        ? (play.state === 'error' ? `Error: ${play.error || play.state}` : 'Waiting for check')
                        : play.apps.length ? play.apps.map((app) => (
                          <div key={app.id}>
                            <a
                              className="text-primary hover:underline" target="_blank" rel="noopener noreferrer"
                              href={`https://play.google.com/store/apps/details?id=${encodeURIComponent(app.id)}&gl=${c.code.toLowerCase()}&hl=${encodeURIComponent(c.playLang)}`}
                            >#{app.position} {app.title}</a>
                            <span className="block text-xs text-content-subtle">{app.developer ? `${app.developer} · ` : ''}{app.id}</span>
                          </div>
                        )) : 'No matches in top 10'}
                      <span className="block text-xs text-content-subtle">Language: {c.playLang}</span>
                      {play?.state === 'done' && (
                        <span className="block text-xs text-content-subtle">{play.count} results · {fmtDate(play.date, { dateStyle: 'short', timeStyle: 'short' })}</span>
                      )}
                    </td>
                  </tr>
                );
              })}
              {!rows.length && <tr><td colSpan={5} className="px-3 py-6 text-center text-content-subtle">No data for this filter. Try showing zero values.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </Modal>
  );
}
